import { colorFor } from '../color';
import type { Graph, GraphPoint, GraphRoute, OnfStatus } from './api';
import type { LatLng } from './types';

/**
 * Walking time in hours (DIN 33466 style, as on the historical site):
 * 4 km/h on the flat, 300 m/h up, 500 m/h down; the largest of the three
 * times plus half of the smallest.
 */
export function walkingDuration(distance: number, up: number, down: number): number {
    const horizontal = distance / 4000;
    const ascent = up / 300;
    const descent = down / 500;
    const max = Math.max(horizontal, ascent, descent);
    const min = Math.min(horizontal, ascent, descent);
    return max + min / 2;
}

export class Point {
    /** Legs leaving this point, in the historical order (forward routes first, then reversed ones). */
    readonly legsOut: Leg[] = [];

    constructor(
        readonly id: string,
        readonly name: string,
        readonly area: string,
        readonly lat: number,
        readonly lng: number,
        readonly hidden: boolean,
    ) {}

    static fromGraph(p: GraphPoint): Point {
        return new Point(p.id, p.name, p.area, p.lat, p.lng, p.hidden === true);
    }

    get location(): LatLng {
        return [this.lat, this.lng];
    }
}

/** A trail between two points, undirected. */
export class Route {
    readonly color: string;
    readonly forward: Leg;
    readonly backward: Leg;

    constructor(
        readonly data: GraphRoute,
        readonly from: Point,
        readonly to: Point,
    ) {
        this.color = colorFor(data.id);
        this.forward = new Leg(this, true);
        this.backward = new Leg(this, false);
    }

    get id(): string {
        return this.data.id;
    }
    get info(): string | undefined {
        return this.data.info || undefined;
    }
    get source(): string {
        return this.data.source;
    }
    get distance(): number {
        return this.data.distance;
    }
    get status(): OnfStatus {
        return this.data.status;
    }
    get closed(): boolean {
        return this.data.status === 'closed';
    }
    get onfManaged(): boolean {
        return this.data.status !== 'unmanaged';
    }
    get cause(): string | undefined {
        return this.data.cause;
    }
    get reopenDate(): string | undefined {
        return this.data.reopen;
    }
    get minAlt(): number {
        return this.data.minAlt;
    }
    get maxAlt(): number {
        return this.data.maxAlt;
    }
    /** Colour on the "all trails" map: blue = not ONF, red = closed, green = open. */
    get statusColor(): string {
        if (!this.onfManaged) return '#0000ff';
        if (this.closed) return '#ff0000';
        return '#00ff9f';
    }
}

/** A route walked in one direction. */
export class Leg {
    /** Stable identifier `<route>-<start>-<end>` (the historical "full code"). */
    readonly code: string;

    constructor(
        readonly route: Route,
        readonly isForward: boolean,
    ) {
        this.code = `${route.id}-${this.start.id}-${this.end.id}`;
    }

    get start(): Point {
        return this.isForward ? this.route.from : this.route.to;
    }
    get end(): Point {
        return this.isForward ? this.route.to : this.route.from;
    }
    get distance(): number {
        return this.route.distance;
    }
    get up(): number {
        return this.isForward ? this.route.data.up : this.route.data.down;
    }
    get down(): number {
        return this.isForward ? this.route.data.down : this.route.data.up;
    }
    get duration(): number {
        return walkingDuration(this.distance, this.up, this.down);
    }
    get closed(): boolean {
        return this.route.closed;
    }
    get info(): string | undefined {
        return this.route.info;
    }
    get color(): string {
        return this.route.color;
    }
    get reverse(): Leg {
        return this.isForward ? this.route.backward : this.route.forward;
    }
    get title(): string {
        return `${this.start.name} → ${this.end.name}${this.info ? ` (${this.info})` : ''}`;
    }
}

/** An itinerary: a sequence of legs from a start point to an end point. */
export class Path {
    /** Stable identifier, the leg codes joined. */
    readonly id: string;
    readonly distance: number;
    readonly duration: number;
    readonly up: number;
    readonly down: number;
    /** Points / routes that no shorter itinerary of the same result set goes through (see `markDistinctive`). */
    distinctivePoints: Point[] = [];
    distinctiveRoutes: Route[] = [];

    constructor(readonly legs: Leg[]) {
        if (legs.length === 0) throw new Error('A path needs at least one leg');
        this.id = legs.map((l) => l.code).join('|');
        this.distance = legs.reduce((acc, l) => acc + l.distance, 0);
        this.duration = legs.reduce((acc, l) => acc + l.duration, 0);
        this.up = legs.reduce((acc, l) => acc + l.up, 0);
        this.down = legs.reduce((acc, l) => acc + l.down, 0);
    }

    get start(): Point {
        return this.legs[0]!.start;
    }
    get end(): Point {
        return this.legs[this.legs.length - 1]!.end;
    }
    /** Start of every leg plus the final point. */
    get points(): Point[] {
        return [...this.legs.map((l) => l.start), this.end];
    }
    get routes(): Route[] {
        return this.legs.map((l) => l.route);
    }
    get hasClosed(): boolean {
        return this.legs.some((l) => l.closed);
    }
    get closedLegs(): Leg[] {
        return this.legs.filter((l) => l.closed);
    }
    get minAlt(): number {
        return Math.min(...this.legs.map((l) => l.route.minAlt));
    }
    get maxAlt(): number {
        return Math.max(...this.legs.map((l) => l.route.maxAlt));
    }
    /** Routes that have an "info" text, in order, without duplicates. */
    get infos(): string[] {
        return [...new Set(this.legs.map((l) => l.info).filter((i): i is string => !!i))];
    }

    /** "Le plus court", "par Marla", "par Marla et Trois Roches"… */
    get title(): string {
        const points = this.distinctivePoints.filter((p) => !p.hidden).map((p) => p.name);
        if (points.length > 0) return `par ${formatNames(points)}`;
        const routes = this.distinctiveRoutes.map((r) => r.to.name);
        if (routes.length > 0) return `par ${formatNames(routes)}`;
        return 'Le plus court';
    }

    static sortByDistance(paths: Path[]): Path[] {
        return [...paths].sort((a, b) => a.distance - b.distance);
    }

    /**
     * Marks, for every path but the first, the points and routes it is the first
     * to use compared with the first (shortest) path. Used for the titles.
     */
    static markDistinctive(paths: Path[]): void {
        const first = paths[0];
        if (!first) return;
        const seenRoutes = new Set(first.routes);
        const seenPoints = new Set(first.points);
        for (const path of paths.slice(1)) {
            path.distinctiveRoutes = path.routes.filter((r) => !seenRoutes.has(r));
            path.distinctivePoints = path.points.filter((p) => !seenPoints.has(p));
        }
    }
}

function formatNames(names: string[]): string {
    if (names.length === 1) return names[0]!;
    return `${names.slice(0, -1).join(', ')} et ${names[names.length - 1]}`;
}

export class Dataset {
    readonly points: Point[] = [];
    readonly routes: Route[] = [];
    readonly lastUpdate: string;
    private readonly pointsById = new Map<string, Point>();
    private readonly routesById = new Map<string, Route>();
    private readonly legsByCode = new Map<string, Leg>();

    constructor(graph: Graph) {
        this.lastUpdate = graph.meta.lastUpdate;
        for (const p of graph.points) {
            const point = Point.fromGraph(p);
            this.points.push(point);
            this.pointsById.set(point.id, point);
        }
        for (const r of graph.routes) {
            const from = this.pointsById.get(r.from);
            const to = this.pointsById.get(r.to);
            if (!from || !to) {
                console.error(`Route ${r.id}: unknown point ${from ? r.to : r.from}`);
                continue;
            }
            const route = new Route(r, from, to);
            this.routes.push(route);
            this.routesById.set(route.id, route);
            this.legsByCode.set(route.forward.code, route.forward);
            this.legsByCode.set(route.backward.code, route.backward);
        }
        // Historical order of the legs leaving a point: forward routes, then reversed ones.
        for (const route of this.routes) route.from.legsOut.push(route.forward);
        for (const route of this.routes) route.to.legsOut.push(route.backward);
    }

    getPoint(id: string): Point | undefined {
        return this.pointsById.get(id);
    }
    getRoute(id: string): Route | undefined {
        return this.routesById.get(id);
    }
    getLeg(code: string): Leg | undefined {
        return this.legsByCode.get(code);
    }

    pathFromCodes(codes: readonly string[]): Path {
        return new Path(
            codes.map((code) => {
                const leg = this.legsByCode.get(code);
                if (!leg) throw new Error(`Unknown leg ${code}`);
                return leg;
            }),
        );
    }

    /** Points grouped by coarse location, in file order. */
    pointsByArea(priorityArea?: string): Map<string, Point[]> {
        const groups = new Map<string, Point[]>();
        if (priorityArea) groups.set(priorityArea, []);
        for (const point of this.points) {
            let group = groups.get(point.area);
            if (!group) {
                group = [];
                groups.set(point.area, group);
            }
            group.push(point);
        }
        if (priorityArea && groups.get(priorityArea)!.length === 0) groups.delete(priorityArea);
        return groups;
    }

    /** Routes grouped by the coarse location of their ends, sorted by start name. */
    routesByArea(): Map<string, Route[]> {
        const groups = new Map<string, Route[]>();
        const add = (area: string, route: Route) => {
            let group = groups.get(area);
            if (!group) {
                group = [];
                groups.set(area, group);
            }
            group.push(route);
        };
        for (const route of this.routes) {
            add(route.from.area, route);
            if (route.from.area !== route.to.area) add(route.to.area, route);
        }
        for (const routes of groups.values()) {
            routes.sort((a, b) => a.from.name.localeCompare(b.from.name, 'fr'));
        }
        return groups;
    }
}
